import { evaluarCalidad, pistaParaLaPersona, type Calidad } from './calidad.js';

/**
 * La captura automática: cuándo disparar la foto sin que nadie pulse nada.
 *
 * Es el "Detección automática..." del Figma. `calidad.ts` responde *¿se puede leer esto?*;
 * aquí se responde *¿es el momento?*.
 *
 * ── POR QUÉ ESTO VIVE EN EL CORE Y NO EN LA PANTALLA ────────────────────────────
 *
 * Es la única decisión del producto que no se puede probar a mano: para comprobarla en la
 * pantalla harían falta una persona, un DNI y una cámara. Aquí recibe números y devuelve una
 * decisión, y se prueba en milisegundos.
 *
 * ── LOS UMBRALES DE VALÍDATE v1 NO SE PUEDEN COPIAR, Y CASI LOS COPIO ──────────
 *
 * v1 (`panel.validate.pe`) dispara con `brightness >= 35` y `sharpness >= 90`. La tentación
 * era traerlos tal cual. **Habría sido un error silencioso**:
 *
 *   · v1 mide nitidez como varianza de un gradiente tipo Sobel sobre un lienzo de 160×120.
 *   · `calidad.ts` la mide como varianza del laplaciano.
 *
 * Dos escalas distintas. El "90" de v1 no significa nada aquí: copiarlo sería poner un número
 * con aspecto de medido que en realidad es de otra regla, y nadie lo habría notado hasta ver
 * que la captura no dispara nunca — o que dispara siempre.
 *
 * Lo que SÍ se copia de v1, porque es tiempo y no escala, es la forma del bucle: mirar cada
 * ~280 ms y exigir varios aciertos seguidos.
 *
 * ── EL NOMBRE `Mirada` NO ES CAPRICHO ──────────────────────────────────────────
 *
 * Esto se llamaba `Veredicto`. Pero `engine/verdict.ts` ya exporta un `Veredicto` —el de
 * identidad, el que ve el cliente— y los dos salen del mismo índice: habría sido un TS2308 y
 * el build muerto. Ya pasó una vez con `evaluar` y `Resultado`.
 *
 * Y el nombre nuevo es mejor: esto no es un veredicto sobre nadie. Es una mirada a un
 * fotograma.
 */

/** Cada cuánto se mira el fotograma. v1 usa 280 ms y le funciona. */
export const MS_ENTRE_MIRADAS = 280;

/**
 * Cuántas miradas buenas SEGUIDAS hacen falta para disparar.
 *
 * Cuatro × 280 ms ≈ 1,1 s de imagen buena sostenida. No es estética: una sola mirada buena la
 * acierta cualquier mano temblorosa por casualidad, justo en el fotograma en que el DNI pasó
 * por delante. Exigir que se sostenga distingue "está encuadrado" de "pasó por ahí".
 */
export const MIRADAS_BUENAS_PARA_DISPARAR = 4;

/**
 * La ráfaga: cuántas fotos de golpe al disparar, y cada cuánto.
 *
 * Se queda la más nítida de las cinco. Es de v1 y es buena idea: el instante exacto en que
 * decides disparar no tiene por qué ser el mejor de ese segundo — y la persona ya está quieta,
 * así que salen casi iguales. Sale gratis y evita una foto movida.
 */
export const FOTOS_DE_LA_RAFAGA = 5;
export const MS_ENTRE_FOTOS_DE_LA_RAFAGA = 110;

/** Qué se intenta capturar. Cambia qué se busca en el fotograma. */
export type Objetivo = 'documento' | 'rostro';

/**
 * Lo que la pantalla ve en un fotograma, ya reducido a números.
 *
 * `hayRostro` significa cosas OPUESTAS según el objetivo, y por eso el tipo es uno y la regla
 * no:
 *
 *   · 'documento' → es la cara IMPRESA del DNI. Es una pista.
 *   · 'rostro'    → es la cara de la persona. Es un requisito.
 *
 * `anchoDelRostro` es la fracción del ancho del encuadre que ocupa la cara (0 a 1): lo que
 * permite distinguir un DNI en la mano de alguien apuntándose a sí mismo.
 */
export interface Observacion {
  /** Un byte por píxel. Lo mismo que come `evaluarCalidad`. */
  grises: Uint8Array;
  ancho: number;
  alto: number;
  hayRostro: boolean;
  anchoDelRostro: number;
}

/**
 * ── LA REGLA QUE EVITA EL SELFIE DEL DNI ────────────────────────────────────────
 *
 * Si la cara ocupa más de la mitad del encuadre cuando pedimos el DNI, no es un DNI: es la
 * persona apuntándose. v1 usa 0.55 y responde "Apunta a tu DNI, no a tu rostro".
 *
 * Parece una tontería hasta que ves los datos: pasa constantemente. Sin esto la captura
 * dispara contra una cara enorme, el OCR no encuentra nada, y la persona no entiende qué hizo
 * mal — así que lo repite igual.
 */
export const ANCHO_MAXIMO_DE_CARA_EN_DOCUMENTO = 0.55;

/** Encuadre del rostro cuando es a la persona a quien buscamos. v1: 0.20–0.80. */
export const ANCHO_MINIMO_DEL_ROSTRO = 0.2;
export const ANCHO_MAXIMO_DEL_ROSTRO = 0.8;

export type MotivoDeEspera =
  | 'sin_rostro'
  | 'demasiado_cerca'
  | 'demasiado_lejos'
  | 'es_tu_cara_no_el_dni'
  | 'calidad';

/** Lo que sale de mirar UN fotograma. No es un juicio sobre nadie. */
export interface Mirada {
  /** ¿Este fotograma cuenta como bueno? */
  bueno: boolean;
  /** Si no, por qué. Para la pista, nunca para el expediente. */
  motivo: MotivoDeEspera | null;
  calidad: Calidad;
}

/**
 * ¿Este fotograma vale?
 *
 * El orden importa: primero lo que la persona arregla moviendo la mano (encuadre), y solo
 * después la calidad. Decirle "poca luz" a quien está apuntando a la pared es darle la pista
 * correcta al problema equivocado.
 */
export function mirar(o: Observacion, objetivo: Objetivo): Mirada {
  const calidad = evaluarCalidad(o.grises, o.ancho, o.alto);

  if (objetivo === 'documento') {
    if (o.hayRostro && o.anchoDelRostro > ANCHO_MAXIMO_DE_CARA_EN_DOCUMENTO) {
      return { bueno: false, motivo: 'es_tu_cara_no_el_dni', calidad };
    }
    // La cara impresa del DNI es una PISTA, no un requisito, y esto es lo que v1 no podía
    // resolver: **el reverso no tiene ninguna cara**. Si exigiéramos verla, el reverso no se
    // capturaría jamás y nadie entendería por qué.
  } else {
    if (!o.hayRostro) return { bueno: false, motivo: 'sin_rostro', calidad };
    if (o.anchoDelRostro > ANCHO_MAXIMO_DEL_ROSTRO)
      return { bueno: false, motivo: 'demasiado_cerca', calidad };
    if (o.anchoDelRostro < ANCHO_MINIMO_DEL_ROSTRO)
      return { bueno: false, motivo: 'demasiado_lejos', calidad };
  }

  if (!calidad.sePuedeUsar) return { bueno: false, motivo: 'calidad', calidad };

  return { bueno: true, motivo: null, calidad };
}

export interface Racha {
  /** Miradas buenas seguidas. */
  seguidas: number;
  /** true justo en el fotograma en que hay que disparar. */
  dispara: boolean;
}

export const RACHA_INICIAL: Racha = { seguidas: 0, dispara: false };

/**
 * Lleva la cuenta de las miradas buenas seguidas.
 *
 * ── UNA MALA NO BORRA LA RACHA: RESTA ──────────────────────────────────────────
 *
 * `seguidas - 1` y no `0`. Poner a cero castiga un parpadeo del detector —un fotograma
 * movido, un reflejo— igual que si la persona hubiera bajado el DNI. Con una mano real, un
 * cero cada tres fotogramas significa que la racha nunca llega a cuatro y la captura **no
 * dispara nunca**: la persona sostiene el DNI perfectamente quieto veinte segundos y la
 * pantalla no hace nada. Ese fallo no da error; simplemente no pasa nada, que es la peor clase.
 *
 * Restar tolera el ruido y sigue castigando lo real: si el DNI se va, las malas se acumulan y
 * la racha baja igual.
 */
export function acumular(r: Racha, bueno: boolean): Racha {
  const seguidas = bueno ? r.seguidas + 1 : Math.max(0, r.seguidas - 1);
  return { seguidas, dispara: seguidas >= MIRADAS_BUENAS_PARA_DISPARAR };
}

/**
 * De la ráfaga, la más nítida.
 *
 * Devuelve el ÍNDICE, no la foto: el core no toca imágenes, solo números. La pantalla ya tiene
 * los JPEG y sabe cuál coger.
 *
 * Con la lista vacía devuelve -1 en vez de reventar. Que la ráfaga salga vacía sería un fallo
 * NUESTRO, y tirarle una excepción en la cara a alguien que solo sostenía su DNI es la peor
 * manera de gestionarlo.
 */
export function laMasNitida(detalles: readonly number[]): number {
  let mejor = -1;
  let max = -Infinity;
  for (let i = 0; i < detalles.length; i++) {
    const d = detalles[i];
    if (d !== undefined && d > max) {
      max = d;
      mejor = i;
    }
  }
  return mejor;
}

/**
 * La pista que ve la persona. UNA sola, la más urgente.
 *
 * Nunca dice "no se detecta el documento" ni nada que suene a que ella falló: dice qué hacer.
 * Es la misma regla de la pantalla de no verificado — qué hacer, nunca por qué falló.
 *
 * Lo de calidad se delega en `pistaParaLaPersona`, que ya existe y ya decidió que la luz va
 * antes que la nitidez (con más luz baja la exposición, y con menos exposición hay menos
 * movimiento borroso). Reescribirla aquí habría sido una segunda copia de esa decisión.
 */
export function pistaDeEncuadre(m: Mirada): string | null {
  if (m.bueno) return null;

  switch (m.motivo) {
    case 'es_tu_cara_no_el_dni':
      return 'Apunta a tu DNI, no a tu rostro';
    case 'sin_rostro':
      return 'Mira a la cámara';
    case 'demasiado_cerca':
      return 'Aléjate un poco';
    case 'demasiado_lejos':
      return 'Acércate un poco';
    case 'calidad':
    case null:
      return pistaParaLaPersona(m.calidad);
  }
}
